'use client';

import { useState, useCallback } from 'react';
import { useApiClient } from '../../contexts/ApiContext';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import type { Bid } from '../../services/generated/Api';
import { BIDS_PER_PAGE, INFINITE_SCROLL_THRESHOLD } from './constants';

interface UseAuctionBidsOptions {
  auctionId?: string;
  setAllBids: React.Dispatch<React.SetStateAction<Bid[]>>;
}

interface UseAuctionBidsResult {
  currentPage: number;
  hasMoreBids: boolean;
  loadingMoreBids: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setHasMoreBids: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useAuctionBids = ({
  auctionId,
  setAllBids,
}: UseAuctionBidsOptions): UseAuctionBidsResult => {
  const apiClient = useApiClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreBids, setHasMoreBids] = useState(true);
  const [loadingMoreBids, setLoadingMoreBids] = useState(false);

  const loadMoreBids = useCallback(async () => {
    if (!auctionId || loadingMoreBids || !hasMoreBids) return;

    setLoadingMoreBids(true);
    try {
      const nextPage = currentPage + 1;
      const response = await apiClient.api.auctionControllerGetAuctionBids({
        auctionId,
        page: nextPage,
        limit: BIDS_PER_PAGE,
      });

      if (response.bids.length > 0) {
        setAllBids((prev) => [...prev, ...response.bids]);
        setCurrentPage(nextPage);
        setHasMoreBids(response.hasMore);
      } else {
        setHasMoreBids(false);
      }
    } catch (error) {
      console.error('Failed to load more bids:', error);
    } finally {
      setLoadingMoreBids(false);
    }
  }, [auctionId, currentPage, hasMoreBids, loadingMoreBids, apiClient, setAllBids]);

  const scrollRef = useInfiniteScroll({
    hasMore: hasMoreBids,
    loading: loadingMoreBids,
    onLoadMore: loadMoreBids,
    threshold: INFINITE_SCROLL_THRESHOLD,
  });

  return {
    currentPage,
    hasMoreBids,
    loadingMoreBids,
    scrollRef,
    setCurrentPage,
    setHasMoreBids,
  };
};
